export interface CityData {
  slug: string;
  name: string;
  county: string;
  lat: number;
  lon: number;
  eventsTracked: number;
  placesIndexed: number;
  councilPlatform: string;
  councilUrl: string;
  metaDescription: string;
  heroHeadline: string;
  heroParagraph: string;
  civicContext: string;
  keyTopics: string[];
}

export const CITIES: CityData[] = [
  {
    slug: 'everett',
    name: 'Everett',
    county: 'Snohomish County',
    lat: 47.979,
    lon: -122.2021,
    eventsTracked: 527,
    placesIndexed: 1596,
    councilPlatform: 'CivicPlus',
    councilUrl: 'https://everettwa.gov/agendacenter',
    metaDescription:
      'Track Everett, WA city council votes, public meetings, and neighborhood updates with Helpped. 527+ civic events monitored via CivicPlus. Free iOS app.',
    heroHeadline: 'Everett city council, tracked in real time',
    heroParagraph:
      'Everett has one of the most active civic calendars in Washington State. Helpped monitors all Everett City Council agenda items and votes via CivicPlus — from Boeing employment policy and waterfront development to transit expansion and school impact fees. With 527 council actions tracked and 1,596 local places mapped across Everett neighborhoods, you get the full picture of what your city is deciding.',
    civicContext:
      "Everett's council meets regularly at Everett City Hall and streams all sessions publicly. Helpped surfaces the agenda items most likely to affect your block — road closures, zoning changes, and budget decisions — before they pass.",
    keyTopics: [
      'Waterfront development and port expansion',
      'Boeing workforce and economic development agreements',
      'Snohomish County transit funding',
      'Housing density and zoning amendments',
    ],
  },
  {
    slug: 'renton',
    name: 'Renton',
    county: 'King County',
    lat: 47.4829,
    lon: -122.2171,
    eventsTracked: 247,
    placesIndexed: 980,
    councilPlatform: 'PrimeGov',
    councilUrl: 'https://rentonwa.gov/city_hall/city_council',
    metaDescription:
      'Track Renton, WA city council votes, road projects, and neighborhood updates with Helpped. 247+ civic events monitored via PrimeGov. Free iOS app.',
    heroHeadline: 'Renton city council, every vote on your phone',
    heroParagraph:
      "Renton City Council is one of King County's most active, managing a growing city straddling the urban-suburban edge. Helpped tracks all Renton council votes and meetings via PrimeGov — covering road construction contracts, budget amendments, stormwater agreements, and regional transit partnerships. 247 council actions have been indexed, including major items like the Logan Avenue S closure and multi-million-dollar consulting contracts that affect daily commutes.",
    civicContext:
      'Renton holds regular city council sessions at City Hall with full public testimony periods. Helpped flags upcoming meetings where public comment is open so you can show up or submit written testimony.',
    keyTopics: [
      'Road construction and major street closures',
      'Cascadia Consulting and sustainability agreements',
      'King County Metro transit coordination',
      'Residential development and permit approvals',
    ],
  },
  {
    slug: 'bellevue',
    name: 'Bellevue',
    county: 'King County',
    lat: 47.6101,
    lon: -122.2015,
    eventsTracked: 130,
    placesIndexed: 2100,
    councilPlatform: 'Legistar',
    councilUrl: 'https://bellevuewa.gov/city-government/city-council',
    metaDescription:
      'Track Bellevue, WA city council votes, infrastructure projects, and neighborhood updates with Helpped. 130+ civic events via Legistar. Free iOS app.',
    heroHeadline: 'Bellevue city council, plain language in your pocket',
    heroParagraph:
      "Bellevue City Council governs one of Washington's wealthiest and fastest-growing cities, making decisions on light rail integration, Spring District development, and cross-lake transportation. Helpped pulls all Bellevue council actions directly from Legistar and translates dense legislative language into plain summaries — so you know what the NE Spring Boulevard Zone 3 professional services agreement actually means for your commute. 130 civic events tracked, 2,100+ local places mapped.",
    civicContext:
      "Bellevue's council meets in the Council Chambers at City Hall (1E-126) and live-streams all sessions. Helpped surfaces items relevant to your Bellevue neighborhood, whether you're in Crossroads, Somerset, or downtown.",
    keyTopics: [
      'East Link light rail and transit integration',
      'Spring District and downtown development',
      'NE Bellevue road and infrastructure projects',
      'Technology sector impact fees and land use',
    ],
  },
  {
    slug: 'olympia',
    name: 'Olympia',
    county: 'Thurston County',
    lat: 47.0379,
    lon: -122.9007,
    eventsTracked: 127,
    placesIndexed: 770,
    councilPlatform: 'Legistar',
    councilUrl: 'https://olympiawa.gov/city-government/city-council',
    metaDescription:
      'Track Olympia, WA city council votes, state capital civic news, and neighborhood updates with Helpped. 127+ civic events via Legistar. Free iOS app.',
    heroHeadline: "Washington's capital city, fully tracked",
    heroParagraph:
      "As Washington's state capital, Olympia sits at the intersection of local governance and state policy. Helpped monitors Olympia City Council via Legistar — tracking 127 council actions on topics from park construction administration to downtown housing — while also surfacing relevant WA State Legislature bills that directly affect Thurston County residents. 770 local places indexed across Olympia neighborhoods.",
    civicContext:
      "Olympia City Council meets at Council Chambers (online and in person) and handles a full slate of civic matters distinct from — but often intersecting with — state legislative activity happening blocks away.",
    keyTopics: [
      'Downtown Olympia housing and shelter policy',
      'Park construction and capital improvements',
      'State agency coordination and land agreements',
      'Thurston County transit and transportation planning',
    ],
  },
  {
    slug: 'auburn',
    name: 'Auburn',
    county: 'King County / Pierce County',
    lat: 47.3073,
    lon: -122.2284,
    eventsTracked: 83,
    placesIndexed: 1377,
    councilPlatform: 'CivicClerk',
    councilUrl: 'https://auburnwa.gov/city_hall/city_council',
    metaDescription:
      'Track Auburn, WA city council votes, public meetings, and neighborhood updates with Helpped. 83+ civic events via CivicClerk. Free iOS app.',
    heroHeadline: 'Auburn city council, every decision on your block',
    heroParagraph:
      "Auburn spans both King and Pierce counties, making it one of the more complex civic environments in the greater Seattle metro. Helpped tracks all Auburn City Council votes and agenda items via CivicClerk — 83 civic actions indexed covering zoning decisions, public works contracts, and neighborhood development that affect Auburn's diverse communities from downtown to the Lea Hill plateau. 1,377 local places mapped across Auburn.",
    civicContext:
      'Auburn City Council holds regular sessions at Auburn City Hall with public hearings open to residents. Helpped was first built in Auburn — it knows this city better than anywhere else.',
    keyTopics: [
      'Auburn downtown revitalization and zoning',
      'King/Pierce county boundary coordination',
      'Public works and infrastructure contracts',
      'Community development and housing approvals',
    ],
  },
  {
    slug: 'redmond',
    name: 'Redmond',
    county: 'King County',
    lat: 47.674,
    lon: -122.1215,
    eventsTracked: 79,
    placesIndexed: 890,
    councilPlatform: 'Legistar',
    councilUrl: 'https://redmond.gov/government/city-council',
    metaDescription:
      'Track Redmond, WA city council votes, tech corridor development, and neighborhood updates with Helpped. 79+ civic events via Legistar. Free iOS app.',
    heroHeadline: 'Redmond city council, beyond the campus bubble',
    heroParagraph:
      "Redmond is home to Microsoft and Nintendo of America, but its city council handles far more than tech campus permits. Helpped tracks 79 Redmond council actions via Legistar — from Overlake urban center development to Sammamish River Trail improvements and regional housing mandates. Whether you live in Redmond Ridge, Education Hill, or near the Connector, Helpped surfaces the decisions that shape your neighborhood before they become law.",
    civicContext:
      'Redmond City Council meets regularly with a focus on balancing tech-driven growth with neighborhood character. Public meetings are open and streamed online.',
    keyTopics: [
      'Overlake urban center and TOD development',
      'Microsoft and tech campus land use agreements',
      'Sammamish River Trail and parks improvements',
      'Regional housing mandates and ADU policy',
    ],
  },
  {
    slug: 'seattle',
    name: 'Seattle',
    county: 'King County',
    lat: 47.6062,
    lon: -122.3321,
    eventsTracked: 74,
    placesIndexed: 5345,
    councilPlatform: 'Legistar',
    councilUrl: 'https://seattle.gov/council',
    metaDescription:
      'Track Seattle, WA city council votes, public meetings, and neighborhood updates with Helpped. 74+ civic events via Legistar, 5,345 local places mapped. Free iOS app.',
    heroHeadline: 'Seattle city council, decoded for your neighborhood',
    heroParagraph:
      "Seattle City Council is one of the most active in the Pacific Northwest, voting on legislation that affects everything from Capitol Hill rents to Rainier Valley small businesses. Helpped pulls all council actions directly from Legistar — including King County Easement Grants, City Light utility agreements, and social housing fund approvals — and surfaces the items relevant to your specific Seattle neighborhood. 5,345 local places are mapped across Seattle, more than any other city on the platform.",
    civicContext:
      "Seattle's council holds sessions at City Hall (600 4th Avenue) with live-stream access. Helpped flags urgent votes and upcoming public comment sessions so you can participate before decisions are made.",
    keyTopics: [
      'Social housing initiative and renter protections',
      'Seattle City Light utility and infrastructure',
      'Neighborhood-specific zoning and density',
      'King County Metro and transit funding',
    ],
  },
  {
    slug: 'tacoma',
    name: 'Tacoma',
    county: 'Pierce County',
    lat: 47.2529,
    lon: -122.4443,
    eventsTracked: 51,
    placesIndexed: 3703,
    councilPlatform: 'Legistar',
    councilUrl: 'https://cityoftacoma.org/government/city-departments/city-clerk/city-council',
    metaDescription:
      'Track Tacoma, WA city council votes, Pierce County civic news, and neighborhood updates with Helpped. 51+ civic events via Legistar, 3,703 places mapped. Free iOS app.',
    heroHeadline: 'Tacoma city council, every vote mapped to your street',
    heroParagraph:
      "Tacoma's city council governs a major port city with a rich industrial history and rapidly changing neighborhoods. Helpped monitors all Tacoma council actions via Legistar — 51 civic events tracked covering port agreements, Hilltop development, transportation infrastructure, and community services contracts. With 3,703 local places mapped across Tacoma — from Proctor to South End — Helpped gives you the most complete picture of what your city is doing.",
    civicContext:
      "Tacoma City Council meets at the Tacoma Municipal Building with regular public meetings and a robust public comment process. Pierce County's broad civic calendar is also tracked by Helpped.",
    keyTopics: [
      'Port of Tacoma and industrial development',
      'Hilltop and Stadium District neighborhood investment',
      'Pierce Transit and transportation agreements',
      'Community services and shelter funding',
    ],
  },
  {
    slug: 'kirkland',
    name: 'Kirkland',
    county: 'King County',
    lat: 47.6815,
    lon: -122.2087,
    eventsTracked: 6,
    placesIndexed: 650,
    councilPlatform: 'PrimeGov',
    councilUrl: 'https://kirklandwa.gov/government/city-council',
    metaDescription:
      'Track Kirkland, WA city council votes, Cross Kirkland Corridor updates, and neighborhood news with Helpped. Civic monitoring via PrimeGov. Free iOS app.',
    heroHeadline: 'Kirkland city council, from downtown to Totem Lake',
    heroParagraph:
      'Kirkland City Council handles one of the most sought-after lakefront cities in Washington, with civic decisions touching everything from the Cross Kirkland Corridor trail to Totem Lake urban center development. Helpped tracks Kirkland council meetings and votes via PrimeGov, surfacing items relevant to your Kirkland neighborhood — whether you live in Juanita, Houghton, or downtown. 650 local places mapped across the city.',
    civicContext:
      'Kirkland City Council meets at City Hall with public sessions and online streaming. Coverage is growing as more agenda items are indexed.',
    keyTopics: [
      'Cross Kirkland Corridor trail and parks',
      'Totem Lake urban center development',
      'Lake Washington shoreline and waterfront access',
      'Regional housing coordination with King County',
    ],
  },
  {
    slug: 'sammamish',
    name: 'Sammamish',
    county: 'King County',
    lat: 47.6163,
    lon: -122.0356,
    eventsTracked: 22,
    placesIndexed: 3663,
    councilPlatform: 'Firecrawl',
    councilUrl: 'https://sammamish.us/government/city-council',
    metaDescription:
      'Track Sammamish, WA city council votes, park development, and neighborhood news with Helpped. 22+ civic events tracked. Free iOS app.',
    heroHeadline: 'Sammamish city council, every park vote and permit',
    heroParagraph:
      "Sammamish is one of Washington's fastest-growing cities, with a city council managing explosive residential development alongside parks, trails, and quality-of-life investments. Helpped tracks 22 Sammamish council actions covering park construction agreements, road improvements, and housing impact fees. With 3,663 local places indexed — the third largest coverage on the platform — Helpped gives Sammamish residents detailed visibility into their community.",
    civicContext:
      'Sammamish City Council holds regular sessions and has recently expanded digital access to meetings. Helpped tracks agenda items as they are published on the city portal.',
    keyTopics: [
      'East Sammamish Parkway and trail improvements',
      'Rapid residential growth and density planning',
      'Park construction and recreation facility agreements',
      'School district coordination and impact fees',
    ],
  },
  {
    slug: 'spokane',
    name: 'Spokane',
    county: 'Spokane County',
    lat: 47.6588,
    lon: -117.426,
    eventsTracked: 10,
    placesIndexed: 1010,
    councilPlatform: 'Firecrawl',
    councilUrl: 'https://spokane.gov/city-council',
    metaDescription:
      'Track Spokane, WA city council votes, civic news, and neighborhood updates with Helpped. Coverage across Spokane and Spokane County. Free iOS app.',
    heroHeadline: 'Spokane city council and county board, both tracked',
    heroParagraph:
      "Spokane sits at the center of Eastern Washington's civic ecosystem. Helpped tracks both Spokane City Council and the Spokane County Board of County Commissioners — giving you civic coverage at both levels in one feed. Spokane County alone has 168 tracked council actions covering public works contracts, county budget amendments, and regional transportation. 1,010 local places are mapped across Spokane neighborhoods from the South Hill to the North Side.",
    civicContext:
      'Spokane City Council meets at City Hall while the Spokane County Board meets at the Public Works Building (1026 W Broadway Ave). Helpped monitors both venues and merges the feeds for Spokane residents.',
    keyTopics: [
      'Spokane County public works and road contracts',
      'Waste reduction and environmental services agreements',
      'Spokane city downtown and West Central development',
      'Eastern Washington regional transit planning',
    ],
  },
];

export function getCityBySlug(slug: string): CityData | undefined {
  return CITIES.find((c) => c.slug === slug);
}
